import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogTitle, DialogContent,
  IconButton, TextField, Button, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GroupIcon from "@mui/icons-material/Group";
import axios from "axios";
import { KeyUri } from "../key";
import { trackEvent } from "../utils/metaPixel";
import { jwtDecode } from 'jwt-decode'; 

function getMetaCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

function getFbc() {
  let fbc = getMetaCookie("_fbc");
  if (!fbc) {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get("fbclid");
    if (fbclid) {
      fbc = `fb.1.${Date.now()}.${fbclid}`;
      document.cookie = `_fbc=${fbc}; path=/; max-age=${90 * 24 * 60 * 60}`;
    }
  }
  return fbc;
}

const TourBookingSheet = ({
  open, onClose, tour,
  travelDate, travellerCount,
  coupon, discount = 0,
  // One entry per itinerary stop: { location_label, hotel, room, nights }.
  // Mirrors TourDetail.jsx's stop-grouped selection — no longer a single hotel.
  selectedHotels = [],
  // Pre-computed by TourDetail.jsx across every stop (already includes each
  // stop's own nights and the room count) — used as-is, not recomputed here.
  hotelAddonTotal = 0,
  selectedDeparture,
    selectedTransport, transportAddon = 0,
  selectedSightseeing = [], sightseeingAddonTotal = 0,
  rooms = 1, roomConfig = [],
  adultsCount = 0, kidsCount = 0,
  kidsDiscountPct = 0, kidsPricePerPerson,
  displayPrice,
  // Hidden platform/misc/additional charges from TourPricing config. TourDetail.jsx
  // folds this into its basePricePerPerson (and therefore into displayPrice /
  // kidsPricePerPerson) before passing props down here — we MUST fold it into our
  // own basePricePerPerson too, or the header price and the actual payable total
  // will silently disagree.
  hiddenChargesPerPerson = 0,
   eventId,
}) => {
  const navigate = useNavigate();
  const [newBookingId, setNewBookingId] = useState(null);
  const [date, setDate] = useState(travelDate || "");
  const guests = travellerCount || 1;
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("razorpay"); // "razorpay" | "cashfree"

  // ── Advance payment state ───────────────────────────────────────────────────
  const [paymentOption, setPaymentOption] = useState("partial"); // "partial" | "full"
  const [advancePercentage, setAdvancePercentage] = useState(30);
  const [advanceBookingDays, setAdvanceBookingDays] = useState(10);

  useEffect(() => {
    if (open) setDate(travelDate || "");
  }, [open, travelDate]);

  // ── Fetch TourPricing config ────────────────────────────────────────────────
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await axios.get(`${KeyUri.BACKEND_URI}/tourpricing`);
        const pricing = res.data?.data;
        if (pricing?.advance_percentage) {
          setAdvancePercentage(Number(pricing.advance_percentage));
        }
        if (pricing?.advance_booking_days) {
          setAdvanceBookingDays(Number(pricing.advance_booking_days));
        }
      } catch (err) {
        console.error("Failed to fetch tour pricing:", err);
      }
    };
    if (open) fetchPricing();
  }, [open]);

  // ── Can pay advance? (date must be > advanceBookingDays away) ──────────────
  const canPayAdvance = useMemo(() => {
    const effectiveDate = selectedDeparture
      ? selectedDeparture.departure_date
      : date;

    if (!effectiveDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tripDate = new Date(effectiveDate);
    tripDate.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil((tripDate - today) / (1000 * 60 * 60 * 24));
    return diffDays > advanceBookingDays;
  }, [date, selectedDeparture, advanceBookingDays]);

  // Force full payment when advance is not allowed
  useEffect(() => {
    if (!canPayAdvance) setPaymentOption("full");
  }, [canPayAdvance]);

  // ── Price calculations ──────────────────────────────────────────────────────
  // basePricePerPerson must include hiddenChargesPerPerson, just like
  // TourDetail.jsx's basePricePerPerson, or adultsRowTotal / totalBookingAmount
  // (the amount actually charged) will fall short of what the header/sidebar
  // price implied.
  const basePricePerPerson = Number(tour?.display_price || tour?.price || 0) + Number(hiddenChargesPerPerson || 0);
// Use the sidebar's number when passed in, so both are guaranteed identical
const headerPrice = typeof displayPrice === "number" ? displayPrice : basePricePerPerson;
  const effectiveKidsPrice = kidsPricePerPerson ?? Math.round(basePricePerPerson * (1 - kidsDiscountPct / 100));
  const adultSubtotal = basePricePerPerson * adultsCount;
  const kidsSubtotal = effectiveKidsPrice * kidsCount;
  const baseSubtotal = adultSubtotal + kidsSubtotal;

 const transportUsageCount = Number(selectedTransport?.usage_count) || 1;

const transportAddonTotal = selectedTransport
  ? selectedTransport.type === "shared"
    ? Number(selectedTransport.price_per_person || transportAddon || 0) * transportUsageCount * Number(guests || 1)
    : Number(selectedTransport.price_per_vehicle || transportAddon || 0) * transportUsageCount
  : 0;

  const gstRate = Number(tour?.gst_rate || 0);
  const serviceFee = 0;
const subtotalBeforeGst = baseSubtotal + hotelAddonTotal + transportAddonTotal + sightseeingAddonTotal;
const adultsRowTotal = adultSubtotal + hotelAddonTotal + transportAddonTotal + sightseeingAddonTotal;
const adultsPricePerPersonWithAddons = adultsCount > 0
  ? Math.round(adultsRowTotal / adultsCount)
  : basePricePerPerson + hotelAddonTotal + transportAddonTotal + sightseeingAddonTotal;
  const subtotalAfterCoupon = Math.max(subtotalBeforeGst - discount, 0);
  const gstAmount = Math.round((subtotalAfterCoupon * gstRate) / 100);
  const totalBeforeDiscount = subtotalBeforeGst + Math.round((subtotalBeforeGst * gstRate) / 100) + serviceFee;
  const finalTotal = subtotalAfterCoupon + gstAmount + serviceFee;

  // ── Advance / remaining amounts ─────────────────────────────────────────────
const totalBookingAmount = Math.round(finalTotal);

// Always compute the actual advance amount regardless of selected option
const computedAdvanceAmount = Math.max(
  Math.round((totalBookingAmount * advancePercentage) / 100), 1
);
const computedRemainingAmount = Math.max(totalBookingAmount - computedAdvanceAmount, 0);

// What actually gets charged depends on the selected option
const advanceAmount = paymentOption === "partial"
  ? computedAdvanceAmount
  : totalBookingAmount;
const remainingAmount = paymentOption === "partial"
  ? computedRemainingAmount
  : 0;
  // ── Shared validation ───────────────────────────────────────────────────────
  const validate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!date) { alert("Please pick a date"); return false; }
    if (new Date(date) < today) { alert("Past dates are not allowed"); return false; }
    if (!firstName.trim()) { alert("Name is required"); return false; }
    if (!/^[A-Za-z\s]+$/.test(firstName)) { alert("Name should contain only alphabets"); return false; }
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) { alert("Enter valid email address"); return false; }
    if (!/^\d{10}$/.test(phone)) { alert("Phone must be exactly 10 digits"); return false; }
    return true;
  };

 const buildBookingPayload = (gatewayFields) => {
  const tokenString = localStorage.getItem('token');
  let userId = null;
  if (tokenString) {
    try {
      const decodedToken = jwtDecode(tokenString);
      userId = decodedToken?.user?.id || null;
    } catch (e) { /* ignore invalid token */ }
  }

  return {
    tourId: tour._id || tour.id,
    date,
    guests: Number(guests),
    firstName,
    lastName: "",
    email,
    phone,
    specialRequests,
    amount: totalBookingAmount,
    advanceAmount,
    remainingAmount,
    paymentType: paymentOption,
    paymentStatus: paymentOption === "partial" ? "partially_paid" : "paid",
    currency: "INR",
    eventId,
    userId,                      
    fbc: getFbc(),                
    fbp: getMetaCookie("_fbp"),   
    ...gatewayFields,
    // One entry per stop the customer chose a hotel for — mirrors
    // TourDetail.jsx's stop-grouped selection instead of a single hotel.
    selected_hotels: (selectedHotels || []).map((sh) => ({
      location_label:       sh.location_label || null,
      hotel_name:           sh.hotel?.hotel_name || "",
      propertyId:           sh.hotel?.propertyId || null,
      property_display_id:  sh.hotel?.property_display_id || "",
      star_rating:           sh.hotel?.star_rating || "",
      room_type:             sh.room?.room_type || "",
      room_name:             sh.room?.room_name || "",
      meal_plan:             sh.room?.meal_plan || "",
      price_per_night:       Number(sh.room?.price_per_night) || 0,
      notes:                 sh.room?.notes || "",
      nights:                Number(sh.nights) || 1,
      rooms_booked:          Number(rooms),
    })),
    hotel_addon_total: hotelAddonTotal || 0,
   selected_transport: selectedTransport ? {
  type:              selectedTransport.type || "shared",
  vehicle:           selectedTransport.vehicle || "",
  capacity:          selectedTransport.capacity || "",
  price_per_person:  Number(selectedTransport.price_per_person) || 0,
  price_per_vehicle: Number(selectedTransport.price_per_vehicle) || 0,
  notes:             selectedTransport.notes || "",
  usage_count:       Number(selectedTransport.usage_count) || 1,
} : null,
    transport_addon_total: transportAddonTotal || 0,
    selected_sightseeing: (selectedSightseeing || []).map((item) => ({
      type:        item.type,
      place_id:    item.place_id || null,
      activity_id: item.activity_id || null,
      name:        item.name || "",
      location:    item.location || "",
      image:       item.image || "",
      price:       Number(item.price) || 0,
    })),
    sightseeing_addon_total: Number(sightseeingAddonTotal) || 0,
    departureId:    selectedDeparture?._id || null,
    departureId:    selectedDeparture?._id || null,
    departure_date: selectedDeparture
      ? new Date(selectedDeparture.departure_date).toISOString().split("T")[0]
      : null,
  };
};
  // ── Post-booking tasks (coupon tracking + navigate) ────────────────────────
  const handlePostBooking = async (bookingId, created) => {
    const bookingReference = created?.bookingRef || bookingId;
    setNewBookingId(bookingId);

      trackEvent("Purchase", {
    value: advanceAmount,
    currency: "INR",
    content_ids: [tour._id || tour.id],
    content_type: "tour_package",
    content_name: tour.title,
  }, eventId);
  
    if (coupon && discount > 0) {
      try {
        const couponCode = coupon.coupon_code || coupon.code || "";
        const token = localStorage.getItem("tokenUser")?.replace(/^\"|\"$/g, "");
        const userId = (localStorage.getItem("userID") || "").replace(/^["']+|["']+$/g, "");
        await axios.post(
          `${KeyUri.BACKEND_URI}/tour-coupon/apply`,
          {
            coupon_code: couponCode.trim(),
            bookingId,
            userId,
            totalAmount: totalBeforeDiscount,
            tourDate: date,
            tourId: tour._id || tour.id,
            bookingRef: bookingReference,
            participants: Number(guests),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
      } catch (couponErr) {
        console.error("Coupon apply error:", couponErr.response?.data || couponErr.message);
      }
    }

    const msg = paymentOption === "partial"
      ? `Advance payment of ₹${advanceAmount.toLocaleString("en-IN")} confirmed! Remaining ₹${remainingAmount.toLocaleString("en-IN")} due before travel.`
      : "Booking confirmed successfully!";

    alert(msg);
    onClose();
    navigate(`/tour-invoice?bookingId=${bookingId}`);
  };

  // ── Razorpay flow ───────────────────────────────────────────────────────────
  const handleRazorpay = async () => {
    if (!window.Razorpay) {
      alert("Razorpay SDK not loaded");
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: advanceAmount * 100,          // ← pay-now amount, not full total
      currency: "INR",
      name: tour.title || "Tour Booking",
      description: tour.location || "Tour",
      image: tour.image_url || "",
      prefill: { name: firstName, email, contact: phone },
      notes: {
        tour_id:        tour._id || tour.id,
        payment_type:   paymentOption,
        total_amount:   String(totalBookingAmount),
        advance_amount: String(advanceAmount),
        balance_due:    String(remainingAmount),
      },
      theme: { color: "#2563eb" },
      handler: async function (response) {
        try {
          const payload = buildBookingPayload({
            paymentGateway:    "razorpay",
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId:   response.razorpay_order_id,
            razorpaySignature: response.razorpay_signature,
          });

          const res = await axios.post(
            `${KeyUri.BACKEND_URI}/create-tour-booking`,
            payload,
            { headers: { "Content-Type": "application/json" } }
          );

          const created = res.data?.data || res.data;
          await handlePostBooking(created?._id, created);
        } catch (err) {
          console.error("Booking save failed:", err);
          alert("Payment succeeded, but booking could not be saved. Please contact support.");
        } finally {
          setSubmitting(false);
        }
      },
      modal: {
        ondismiss: () => setSubmitting(false),
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // ── Cashfree flow ───────────────────────────────────────────────────────────
  const handleCashfree = async () => {
    const orderRes = await axios.post(
      `${KeyUri.BACKEND_URI}/tour-booking/create-cashfree-order`,
      {
        tourId: tour._id || tour.id,
        amount: advanceAmount,            // ← pay-now amount
        firstName,
        lastName: "",
        email,
        phone,
        guests: Number(guests),
      },
      { headers: { "Content-Type": "application/json" } }
    );

    const { order_id, payment_session_id } = orderRes.data;

    if (!payment_session_id) {
      throw new Error("Cashfree: no payment_session_id returned");
    }

    if (!window.Cashfree) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
        script.onload = resolve;
        script.onerror = () => reject(new Error("Cashfree SDK failed to load"));
        document.body.appendChild(script);
      });
    }

    const cashfree = window.Cashfree({
      mode: (process.env.REACT_APP_CASHFREE_ENV || "SANDBOX").toLowerCase(),
    });

    await new Promise((resolve, reject) => {
      cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_modal",
      }).then(async (result) => {
        if (result.error) {
          reject(new Error(result.error.message || "Cashfree payment failed"));
          return;
        }

        try {
          const verifyRes = await axios.post(
            `${KeyUri.BACKEND_URI}/tour-booking/verify-cashfree-payment`,
            { orderId: order_id },
            { headers: { "Content-Type": "application/json" } }
          );

          if (!verifyRes.data.success) {
            reject(new Error("Payment verification failed"));
            return;
          }

          const payload = buildBookingPayload({
            paymentGateway:    "cashfree",
            cashfreeOrderId:   order_id,
            cashfreePaymentId: verifyRes.data.cf_order_id?.toString() || order_id,
          });

          const bookingRes = await axios.post(
            `${KeyUri.BACKEND_URI}/create-tour-booking`,
            payload,
            { headers: { "Content-Type": "application/json" } }
          );

          const created = bookingRes.data?.data || bookingRes.data;
          await handlePostBooking(created?._id, created);
          resolve();
        } catch (err) {
          reject(err);
        }
      }).catch(reject);
    });
  };

    // ── HDFC flow ────────────────────────────────────────────────────────────
  const handleHdfc = async () => {
    // Create the booking first (pending), since HDFC redirects off-page —
    // unlike Razorpay/Cashfree, there's no modal to come back to.
    const payload = buildBookingPayload({
      paymentGateway: "hdfc",
      paymentStatus: "pending",
    });
    // override: don't mark as paid yet, HDFC verify will do that on return
    payload.paymentStatus = "pending";

    const bookingRes = await axios.post(
      `${KeyUri.BACKEND_URI}/create-tour-booking`,
      payload,
      { headers: { "Content-Type": "application/json" } }
    );

    const created = bookingRes.data?.data || bookingRes.data;
    const bookingId = created?._id;
    if (!bookingId) throw new Error("Could not create booking before payment");

    const orderRes = await axios.post(
      `${KeyUri.BACKEND_URI}/payments/hdfc/create-order`,
      { type: "tour", bookingId },
      { headers: { "Content-Type": "application/json" } }
    );

    const { payment_url } = orderRes.data;
    if (!payment_url) throw new Error("HDFC did not return a payment URL");

        if (coupon && discount > 0) {
      sessionStorage.setItem("hdfc_tour_coupon", JSON.stringify({
        coupon_code: (coupon.coupon_code || coupon.code || "").trim(),
        bookingId,
        userId: (localStorage.getItem("userID") || "").replace(/^["']+|["']+$/g, ""),
        totalAmount: totalBeforeDiscount,
        tourDate: date,
        tourId: tour._id || tour.id,
        participants: Number(guests),
      }));
    }
    
    // Full-page redirect — this leaves the React app
    window.location.href = payment_url;
  };

  // ── Main confirm handler ────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!validate()) return;

    try {
      setSubmitting(true);

      if (paymentMethod === "cashfree") {
        await handleCashfree();
      } else if (paymentMethod === "hdfc") {
        await handleHdfc();
        return; // page is navigating away, no need to reset submitting
      } else {
        await handleRazorpay();
        return;
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Unable to start payment. Please try again.");
    } finally {
            if (paymentMethod !== "razorpay") setSubmitting(false);
    }
  };

  if (!tour) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, p: 0 } }}
    >
      <DialogTitle sx={{ fontSize: 20, fontWeight: 600, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Book Your Tour
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Tour header */}
        <div className="flex gap-3 mb-4">
          <img
            src={Array.isArray(tour.image_url) ? tour.image_url[0] : tour.image_url}
            alt={tour.title}
            style={{ width: 96, height: 96, borderRadius: 16, objectFit: "cover" }}
          />
       <div>
  <h3 className="text-lg font-semibold">{tour.title}</h3>
  <p className="text-sm text-gray-600">{tour.location}</p>
  <p className="text-sm text-blue-600 font-medium mt-1">
    ₹{headerPrice.toLocaleString("en-IN")} per person
  </p>
</div>
        </div>

        <Divider sx={{ my: 2 }} />

        {/* Date */}
        <p className="text-sm font-medium mb-1 text-gray-700">Select Date</p>
        <TextField
          fullWidth type="date" value={date}
          onChange={(e) => setDate(e.target.value)}
          inputProps={{
            min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString().split("T")[0],
          }}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <CalendarTodayIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
            ),
          }}
          sx={{ mb: 3 }}
        />

        {/* Guests & Rooms */}
        <div className="mb-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <GroupIcon fontSize="small" sx={{ color: "text.secondary" }} />
            Guests & Rooms
          </p>
          <div className="space-y-1">
            {roomConfig.length > 0 ? (
              roomConfig.map((room, idx) => (
                <div key={idx} className="flex justify-between text-sm text-gray-600">
                  <span>Room {idx + 1}</span>
                  <span>
                    {room.adults} Adult{room.adults !== 1 ? "s" : ""}
                    {room.children > 0 ? `, ${room.children} Child${room.children !== 1 ? "ren" : ""}` : ""}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-600">{guests} Guest{guests !== 1 ? "s" : ""}</p>
            )}
            <div className="flex justify-between text-sm font-medium text-gray-700 pt-1 border-t border-gray-200 mt-1">
              <span>Total</span>
              <span>{rooms} Room{rooms !== 1 ? "s" : ""} · {guests} Guest{guests !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>

        {/* Selected hotels, one line per stop */}
        {selectedHotels.length > 0 && (
          <div className="mb-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-sm font-medium text-gray-700 mb-2">Your Stays</p>
            <div className="space-y-1.5">
              {selectedHotels.map((sh, i) => (
                <div key={i} className="flex justify-between text-sm text-gray-600">
                  <span>
                    {sh.location_label ? `${sh.location_label}: ` : ''}
                    {sh.hotel?.hotel_name || 'Hotel'}
                    {sh.room?.room_name ? ` - ${sh.room.room_name}` : ''}
                  </span>
                  <span className="text-gray-500">{sh.nights} night{sh.nights !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Guest info */}
        <TextField label="Guest Name" fullWidth margin="normal" value={firstName}
          onChange={(e) => { if (/^[A-Za-z\s]*$/.test(e.target.value)) setFirstName(e.target.value); }}
        />
        <TextField label="Email" type="email" fullWidth margin="normal" value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Phone" type="tel" fullWidth margin="normal" value={phone}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "");
            if (v.length <= 10) setPhone(v);
          }}
          inputProps={{ maxLength: 10 }}
        />
        <TextField
          label="Special Requests (optional)" fullWidth margin="normal"
          multiline rows={3} value={specialRequests}
          onChange={(e) => setSpecialRequests(e.target.value)}
        />

        <Divider sx={{ my: 2 }} />

        {/* Price breakdown */}
        <div className="space-y-2 text-sm mb-4 p-3 bg-gray-50 rounded-xl">
          <div className="flex justify-between font-medium">
            <span>Adults ({adultsCount} × ₹{adultsPricePerPersonWithAddons.toLocaleString("en-IN")})</span>
            <span>₹{adultsRowTotal.toLocaleString("en-IN")}</span>
          </div>
          {kidsCount > 0 && (
            <div className="flex justify-between text-blue-600">
              <span>
                Children ({kidsCount} × ₹{effectiveKidsPrice.toLocaleString("en-IN")}
                {kidsDiscountPct > 0 && ` · ${kidsDiscountPct}% off`})
              </span>
              <span>₹{kidsSubtotal.toLocaleString("en-IN")}</span>
            </div>
          )}
          {serviceFee > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>Service Fee</span>
              <span>+ ₹{serviceFee.toLocaleString("en-IN")}</span>
            </div>
          )}
          {discount > 0 && (
            <div className="flex justify-between text-green-600">
              <span>Coupon Discount</span>
              <span>- ₹{discount.toLocaleString("en-IN")}</span>
            </div>
          )}
          {gstRate > 0 && (
            <div className="flex justify-between text-gray-600">
              <span>GST ({gstRate}%)</span>
              <span>+ ₹{gstAmount.toLocaleString("en-IN")}</span>
            </div>
          )}
          <Divider sx={{ my: 1 }} />
          <div className="flex justify-between items-center font-bold text-base">
            <span>Total Payable</span>
            <span className="text-blue-700">₹{totalBookingAmount.toLocaleString("en-IN")}</span>
          </div>

          {/* Advance breakdown — only show when partial */}
          {paymentOption === "partial" && (
            <>
              <div className="flex justify-between text-blue-700 font-semibold">
                <span>Pay Now ({advancePercentage}% advance)</span>
                <span>₹{advanceAmount.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-orange-600">
                <span>Balance Due Before Travel</span>
                <span>₹{remainingAmount.toLocaleString("en-IN")}</span>
              </div>
            </>
          )}
        </div>

        {/* ── Payment Option selector ─────────────────────────────────────── */}
        <div className="mb-4 border border-gray-200 rounded-xl p-3 bg-gray-50">
          <p className="text-sm font-semibold text-gray-800 mb-3">Choose Payment Option</p>

          {/* Advance option */}
          <label className={`flex items-start gap-3 p-3 border rounded-lg mb-2 bg-white transition-all
            ${canPayAdvance ? "cursor-pointer hover:border-blue-300" : "opacity-50 cursor-not-allowed"}`}>
            <input
              type="radio"
              name="paymentOption"
              value="partial"
              checked={paymentOption === "partial"}
              onChange={() => canPayAdvance && setPaymentOption("partial")}
              disabled={!canPayAdvance}
              className="mt-1 accent-blue-600"
            />
            <div>
              <p className="font-medium text-gray-900 text-sm">Pay Advance Now</p>
              {canPayAdvance ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  Pay ₹{computedAdvanceAmount.toLocaleString("en-IN")} now and ₹{computedRemainingAmount.toLocaleString("en-IN")} before travel
                </p>
              ) : (
                <p className="text-xs text-red-500 mt-0.5">
                  Not available - trip is within {advanceBookingDays} days. Full payment required.
                </p>
              )}
            </div>
          </label>

          {/* Full payment option */}
          <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer bg-white hover:border-blue-300 transition-all">
            <input
              type="radio"
              name="paymentOption"
              value="full"
              checked={paymentOption === "full"}
              onChange={() => setPaymentOption("full")}
              className="mt-1 accent-blue-600"
            />
            <div>
              <p className="font-medium text-gray-900 text-sm">Pay Full Amount</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Pay ₹{totalBookingAmount.toLocaleString("en-IN")} now
              </p>
            </div>
          </label>
        </div>

        {/* ── Payment method selector ─────────────────────────────────────── */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Payment Method</p>
           <div className="grid grid-cols-3 gap-3">

            {/* Razorpay */}
            <button
              type="button"
              onClick={() => setPaymentMethod("razorpay")}
              className={`flex flex-col items-center gap-2 border-2 rounded-xl p-3 transition-all
                ${paymentMethod === "razorpay"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"}`}
            >
              <img
                src="https://razorpay.com/favicon.png"
                alt="Razorpay"
                className="w-6 h-6 object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <span className="text-xs font-semibold text-gray-700">Razorpay</span>
              <span className="text-[10px] text-gray-400">Cards, UPI, NetBanking</span>
              {paymentMethod === "razorpay" && (
                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                  Selected
                </span>
              )}
            </button>

            {/* Cashfree */}
            <button
              type="button"
              onClick={() => setPaymentMethod("cashfree")}
              className={`flex flex-col items-center gap-2 border-2 rounded-xl p-3 transition-all
                ${paymentMethod === "cashfree"
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300"}`}
            >
              <img
                src="https://cashfree.com/favicon.ico"
                alt="Cashfree"
                className="w-6 h-6 object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <span className="text-xs font-semibold text-gray-700">Cashfree</span>
              <span className="text-[10px] text-gray-400">UPI, Cards, Wallets</span>
              {paymentMethod === "cashfree" && (
                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                  Selected
                </span>
              )}
            </button>

  {/* HDFC */}
           {/*} <button
              type="button"
              onClick={() => setPaymentMethod("hdfc")}
              className={`flex flex-col items-center gap-2 border-2 rounded-xl p-3 transition-all
                ${paymentMethod === "hdfc"
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-200 hover:border-gray-300"}`}
            >
              <span className="text-xs font-semibold text-gray-700">HDFC Bank</span>
              <span className="text-[10px] text-gray-400">Cards, UPI, NetBanking</span>
              {paymentMethod === "hdfc" && (
                <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                  Selected
                </span>
              )}
            </button>*/}
            
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-2">
          <Button variant="outlined" fullWidth onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained" fullWidth color="primary"
            onClick={handleConfirm} disabled={submitting}
          >
            {submitting
              ? "Processing..."
              : paymentOption === "partial"
                ? `Pay Advance ₹${advanceAmount.toLocaleString("en-IN")}`
                : `Pay ₹${totalBookingAmount.toLocaleString("en-IN")}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TourBookingSheet;